import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createPayment, getBooking } from '../services/api';
import LoadingSpinner from './LoadingSpinner';

function Payment() {
  const { bookingId, merchantSlug } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchBooking = async () => {
    try {
      const response = await getBooking(bookingId, merchantSlug);
      setBooking(response.data);
    } catch (error) {
      console.error('Error fetching booking:', error);
      setError('無法載入體驗資訊');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBooking();
  }, [bookingId]);

  const handlePayment = async () => {
    setPaymentLoading(true);
    setError('');

    try {
      const response = await createPayment(bookingId);
      
      if (response.data.success && response.data.payment_url) {
        // 重定向到 LINE Pay 付款頁面
        window.location.href = response.data.payment_url;
      } else {
        setError(response.data.error || '建立付款請求失敗');
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      setError(error.response?.data?.error || '付款請求失敗，請稍後再試');
    } finally {
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <span className="material-symbols-outlined text-6xl text-gray-400">error</span>
        <p className="text-text-primary text-lg">找不到體驗資訊</p>
        <button
          onClick={() => navigate('/bookings')}
          className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
        >
          返回體驗列表
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 p-4 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => navigate(`/confirmation/${bookingId}`)}
          className="flex items-center gap-2 text-text-primary"
        >
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <h1 className="text-xl font-bold text-text-light dark:text-text-dark">付款</h1>
      </div>

      {/* Payment Summary */}
      <div className="p-6">
        <div className="bg-white dark:bg-background-dark rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-lg font-semibold text-text-light dark:text-text-dark mb-4">付款摘要</h2>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">服務項目</span>
              <span className="text-text-light dark:text-text-dark font-medium">{booking.service_name}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">體驗日期</span>
              <span className="text-text-light dark:text-text-dark">{booking.booking_date}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">體驗時間</span>
              <span className="text-text-light dark:text-text-dark">{booking.booking_time}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">聯絡人</span>
              <span className="text-text-light dark:text-text-dark">{booking.customer_name}</span>
            </div>
            
            <hr className="border-gray-200 dark:border-gray-700" />
            
            <div className="flex justify-between text-lg font-semibold">
              <span className="text-text-light dark:text-text-dark">總金額</span>
              <span className="text-primary">NT$ {booking.total_amount}</span>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-red-500">error</span>
              <p className="text-red-700 dark:text-red-400">{error}</p>
            </div>
          </div>
        )}

        {/* Payment Method */}
        <div className="bg-white dark:bg-background-dark rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h3 className="text-lg font-semibold text-text-light dark:text-text-dark mb-4">付款方式</h3>
          
          <div className="flex items-center gap-4 p-4 border border-green-200 dark:border-green-800 rounded-lg bg-green-50 dark:bg-green-900/20">
            <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
              <span className="material-symbols-outlined text-white">payment</span>
            </div>
            <div>
              <p className="font-medium text-text-light dark:text-text-dark">LINE Pay</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">安全便捷的線上付款</p>
            </div>
          </div>
        </div>

        {/* Payment Button */}
        <button
          onClick={handlePayment}
          disabled={paymentLoading}
          className="w-full h-14 flex items-center justify-center bg-primary text-white text-lg font-bold rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200 disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
        >
          {paymentLoading ? (
            <div className="flex items-center gap-2">
              <LoadingSpinner size="sm" />
              <span>處理中...</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined">payment</span>
              <span>前往 LINE Pay 付款</span>
            </div>
          )}
        </button>

        {/* Security Notice */}
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-start gap-2">
            <span className="material-symbols-outlined text-blue-500 mt-0.5">security</span>
            <div>
              <p className="text-sm font-medium text-blue-700 dark:text-blue-400">安全付款保障</p>
              <p className="text-xs text-blue-600 dark:text-blue-500 mt-1">
                您的付款資訊將透過 LINE Pay 安全加密處理，我們不會儲存您的信用卡資訊。
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Payment;